import axios from 'axios'

// Cria instância do Axios
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3001',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Instância pública, sem interceptors - pras rotas de driver-link, que não usam
// sessão (token vem na URL, não em Authorization). Sem isso, o interceptor de
// request anexaria o Bearer de quem estiver logado no aparelho (o próprio
// vendedor testando no celular dele mesmo, por exemplo), e o de response
// mandaria a página inteira pra /login em qualquer 401 vindo de outra aba -
// nenhum dos dois faz sentido numa página pública sem conceito de sessão.
export const publicApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3001',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Chaves do localStorage
export const ACCESS_TOKEN_KEY = 'entregafacil_access_token'
const REFRESH_TOKEN_KEY = 'entregafacil_refresh_token'

// Helpers para gerenciar tokens
export const tokenStorage = {
  getAccessToken: () => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  
  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  },
  
  clearTokens: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  },
}

// Flag para evitar múltiplas tentativas de refresh simultâneas
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

// Interceptor de REQUEST - adiciona token
api.interceptors.request.use(
  (config) => {
    const token = tokenStorage.getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Interceptor de RESPONSE - trata 401 e faz refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Se não for 401 ou já tentou refresh, rejeita
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error)
    }

    // Se é a rota de refresh que falhou, faz logout
    if (originalRequest.url?.includes('/auth/refresh')) {
      tokenStorage.clearTokens()
      window.location.href = '/login'
      return Promise.reject(error)
    }

    // Se já está fazendo refresh, adiciona à fila
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return api(originalRequest)
        })
        .catch((err) => Promise.reject(err))
    }

    originalRequest._retry = true
    isRefreshing = true

    try {
      const refreshToken = tokenStorage.getRefreshToken()
      
      if (!refreshToken) {
        throw new Error('No refresh token')
      }

      // Chama endpoint de refresh
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/auth/refresh`,
        { refreshToken }
      )

      const { accessToken, refreshToken: newRefreshToken } = response.data.data

      // Salva novos tokens
      tokenStorage.setTokens(accessToken, newRefreshToken)

      // Processa fila de requests que falharam
      processQueue(null, accessToken)

      // Refaz request original com novo token
      originalRequest.headers.Authorization = `Bearer ${accessToken}`
      return api(originalRequest)

    } catch (refreshError) {
      processQueue(refreshError, null)
      tokenStorage.clearTokens()
      window.location.href = '/login'
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  }
)

export default api
